import { ComponentVerticalPlacementType } from "building-types/components/componentInstanceTypes";
import type {
  ComponentTransform,
  ComponentTransformVerticalPlacementReplication,
} from "building-types/components/componentTypes";
import type { LevelHandle } from "building-types/handles/docEntityHandleTypes";
import type { LevelDef } from "building-types/levels/LevelDef";
import type { Level } from "building-types/levels/levelTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import type { RWTable, Table } from "common/data/table";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import type { LinkedLevelWithResolvedOffset } from "../generateLevelsTools";
import { generateLevelDatums } from "../generateLevelsTools";

export function createLevelComponentInstances(
  docLevelId: string,
  sourceLevelDef: LevelDef | undefined,
  levelDef: LinkedLevelWithResolvedOffset,
  generatedLevel: Level,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  componentToTransforms: Table<readonly ComponentTransform[]>,
  generatedLevels: RWTable<Level>,
  foundationSettings?: FoundationSystemSettings,
): void {
  // helper function to iterate through all the component instance transforms
  const iterateTransforms = (
    levelId: string | undefined,
    callback: (
      levelId: string,
      componentInstanceChain: string[],
      replicationPlacement: ComponentTransformVerticalPlacementReplication,
    ) => void,
  ) => {
    if (!levelId) {
      return;
    }

    // look for the levelId in the component transforms sourceLevels
    for (const componentId in componentToTransforms) {
      const transforms = componentToTransforms[componentId] || [];
      for (const transform of transforms) {
        const { componentInstanceChain, verticalPlacement, sourceLevels } = transform;
        if (!sourceLevels.has(levelId)) {
          continue;
        }

        const { type } = verticalPlacement;

        // Only replication vertical placement creates ephemeral levels
        // (Remapping type reuses existing levels, so no need to create ephemeral levels)
        if (type !== ComponentVerticalPlacementType.REPLICATION) {
          continue;
        }

        callback(levelId, componentInstanceChain, verticalPlacement);
      }
    }
  };

  // Right now, only levels added to components are instanciated. When instanciating a level, we need to
  // check if the level above or bellow will be also instantiated. If so, the current instance level will
  // point to the instantiated level above or bellow. If not, the current instance level will point to the original
  // level above or bellow. The following map will store the stable ids of the levels above and bellow that will be instantiated.
  const levelsToBeInstantiated: RWTable<boolean> = {};

  iterateTransforms(
    levelDef.levelAbove,
    (
      levelId: string,
      componentInstanceChain: string[],
      _replicationPlacement: ComponentTransformVerticalPlacementReplication,
    ) => {
      const instanceLevelId = instanceIdForComponent(levelId, componentInstanceChain);
      levelsToBeInstantiated[instanceLevelId] = true;
    },
  );

  iterateTransforms(
    levelDef.levelBelow,
    (
      levelId: string,
      componentInstanceChain: string[],
      _replicationPlacement: ComponentTransformVerticalPlacementReplication,
    ) => {
      const instanceLevelId = instanceIdForComponent(levelId, componentInstanceChain);
      levelsToBeInstantiated[instanceLevelId] = true;
    },
  );

  // instantiate the level
  iterateTransforms(
    docLevelId,
    (
      levelId: string,
      componentInstanceChain: string[],
      replicationPlacement: ComponentTransformVerticalPlacementReplication,
    ) => {
      if (sourceLevelDef) {
        const evaluatedLevelDef = evaluateEntityForComponentInstance(
          sourceLevelDef,
          componentInstanceChain,
          definitions,
          optionChoices,
        );
        if (evaluatedLevelDef.disabled) {
          return;
        }
      }

      const { offset } = replicationPlacement;
      const instanceLevelId = instanceIdForComponent(levelId, componentInstanceChain);

      const handle: LevelHandle = {
        type: "LEVEL",
        id: instanceLevelId,
      };

      let levelAbove: string | undefined;
      let levelBelow: string | undefined;

      // Link only to sibling levels replicated within this same component instance. If the neighbor
      // is not instantiated here, leave the link undefined rather than pointing at the host/global
      // level — otherwise level-below/above consumers (railings, stairs, foundation/underlay plans)
      // would pull content from the global stack (e.g. global Level 1) into the instance's plan.
      if (generatedLevel.levelAbove) {
        const potentialLevelAbove = instanceIdForComponent(generatedLevel.levelAbove, componentInstanceChain);
        levelAbove = levelsToBeInstantiated[potentialLevelAbove] ? potentialLevelAbove : undefined;
      }

      if (generatedLevel.levelBelow) {
        const potentialLevelBelow = instanceIdForComponent(generatedLevel.levelBelow, componentInstanceChain);
        levelBelow = levelsToBeInstantiated[potentialLevelBelow] ? potentialLevelBelow : undefined;
      }

      const floorElevation = worldInchesNew(generatedLevel.floorElevation.valueOf() + offset);
      const { floorPlateThickness } = generatedLevel;
      const { isAboveGround } = levelDef;
      const refLevel = isAboveGround ? generatedLevels[levelBelow ?? ""] : generatedLevels[levelAbove ?? ""];

      const levelDatumsGenerated = generateLevelDatums(
        levelDef,
        worldInchesNew(offset),
        floorElevation,
        refLevel,
        floorPlateThickness,
        generatedLevels,
        foundationSettings,
      );

      generatedLevels[instanceLevelId] = {
        ...generatedLevel,
        id: instanceLevelId,
        handle,
        levelAbove,
        levelBelow,
        floorElevation,
        levelDatumsGenerated,
        componentInstanceChain,
        docLevelId,
      };
    },
  );
}
